import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Headphones } from 'lucide-react';

/**
 * Stage 3 — The Deep
 * Synthesized binaural beat: 200Hz carrier, ~5Hz beat (Alpha/Theta drop)
 * Left ear: 200Hz · Right ear: 205Hz (5Hz binaural beat)
 * Built with Web Audio API — no audio file needed.
 */
export default function BinauralDeep() {
  const [playing, setPlaying] = useState(false);
  const [carrier, setCarrier] = useState(200); // Hz
  const [beat, setBeat] = useState(5); // Hz (Alpha/Theta boundary)
  const [volume, setVolume] = useState(0.25);
  const [elapsed, setElapsed] = useState(0);

  const ctxRef = useRef(null);
  const oscLRef = useRef(null);
  const oscRRef = useRef(null);
  const gainRef = useRef(null);
  const mergerRef = useRef(null);
  const intervalRef = useRef(null);

  const stop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    try { oscLRef.current?.stop(); } catch (e) { /* noop */ }
    try { oscRRef.current?.stop(); } catch (e) { /* noop */ }
    try { ctxRef.current?.close(); } catch (e) { /* noop */ }
    ctxRef.current = null;
    setPlaying(false);
    setElapsed(0);
  };

  const start = () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) {
      alert('Your browser does not support Web Audio API.');
      return;
    }
    const ctx = new AudioCtx();
    ctxRef.current = ctx;

    const merger = ctx.createChannelMerger(2);
    mergerRef.current = merger;

    const gain = ctx.createGain();
    gain.gain.value = volume;
    gainRef.current = gain;

    const oscL = ctx.createOscillator();
    oscL.frequency.value = carrier;
    oscL.type = 'sine';
    oscL.connect(merger, 0, 0);
    oscLRef.current = oscL;

    const oscR = ctx.createOscillator();
    oscR.frequency.value = carrier + beat;
    oscR.type = 'sine';
    oscR.connect(merger, 0, 1);
    oscRRef.current = oscR;

    merger.connect(gain).connect(ctx.destination);

    oscL.start();
    oscR.start();

    setPlaying(true);
    setElapsed(0);
    intervalRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
  };

  // Live update gain & frequencies if user adjusts while playing
  useEffect(() => {
    if (gainRef.current) gainRef.current.gain.value = volume;
  }, [volume]);

  useEffect(() => {
    if (oscLRef.current) oscLRef.current.frequency.value = carrier;
    if (oscRRef.current) oscRRef.current.frequency.value = carrier + beat;
  }, [carrier, beat]);

  useEffect(() => () => stop(), []);

  const toggle = () => (playing ? stop() : start());

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div
      className="mystical-card p-8 relative overflow-hidden animate-fade-up"
      data-testid="binaural-deep-card"
      style={{ background: 'linear-gradient(135deg, rgba(31,33,32,0.06) 0%, rgba(122,130,116,0.04) 100%)' }}
    >
      {/* Floating orbs */}
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full" style={{ background: 'radial-gradient(circle, rgba(198,178,143,0.2), transparent 70%)' }} />
      <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full" style={{ background: 'radial-gradient(circle, rgba(122,130,116,0.15), transparent 70%)' }} />

      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Headphones size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.3em] uppercase">Stage 3 · The Deep</span>
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl text-dark leading-tight mb-2 mystical-heading">Alpha–Theta Binaural Drop</h3>
        <p className="font-sans text-sm text-text-muted leading-relaxed mb-6 max-w-xl">
          A {beat}Hz binaural carrier on a {carrier}Hz tone. Use stereo headphones. Let the analytical mind soften — the Code becomes visible underneath.
        </p>

        <div className="flex items-center gap-5 mb-6">
          <button
            onClick={toggle}
            data-testid="binaural-toggle"
            className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${
              playing ? 'bg-gold text-dark' : 'bg-dark text-bone'
            }`}
            style={{ transition: 'background-color 0.3s ease', boxShadow: playing ? '0 0 24px rgba(198,178,143,0.4)' : 'none' }}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
          </button>
          <div className="flex-1">
            <p className="font-serif text-lg text-dark" data-testid="binaural-elapsed">{fmt(elapsed)}</p>
            <p className="font-sans text-xs text-text-muted">{playing ? 'Drop in. Stay until the mind softens.' : 'Headphones recommended.'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-5 border-t border-black/8">
          <div>
            <label className="text-text-muted text-[10px] tracking-[0.2em] uppercase font-sans block mb-2">
              Carrier · {carrier}Hz
            </label>
            <input
              type="range"
              min={120}
              max={300}
              step={10}
              value={carrier}
              onChange={(e) => setCarrier(parseInt(e.target.value))}
              data-testid="binaural-carrier"
              className="w-full accent-[#C6B28F]"
            />
          </div>
          <div>
            <label className="text-text-muted text-[10px] tracking-[0.2em] uppercase font-sans block mb-2">
              Beat · {beat}Hz {beat <= 4 ? '(Theta)' : beat <= 8 ? '(Alpha)' : '(Beta)'}
            </label>
            <input
              type="range"
              min={3}
              max={10}
              step={0.5}
              value={beat}
              onChange={(e) => setBeat(parseFloat(e.target.value))}
              data-testid="binaural-beat"
              className="w-full accent-[#C6B28F]"
            />
          </div>
          <div>
            <label className="text-text-muted text-[10px] tracking-[0.2em] uppercase font-sans block mb-2">
              Volume · {Math.round(volume * 100)}%
            </label>
            <input
              type="range"
              min={0}
              max={0.6}
              step={0.05}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              data-testid="binaural-volume"
              className="w-full accent-[#C6B28F]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
